import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, RefreshCw, Link2Off, Lock } from 'lucide-react';
import { CartellaVista } from '../components/haccp/ispettore/CartellaVista';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pubblico/ispettore`;
const ora = (iso) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

const LinkNonValido = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
    <div className="max-w-sm bg-white rounded-2xl border border-slate-200 p-6 space-y-2" data-testid="inspector-invalid-token-alert" role="alert">
      <Link2Off className="h-8 w-8 text-rose-600" />
      <h1 className="text-xl font-extrabold text-slate-950">Link non valido</h1>
      <p className="text-sm text-slate-600">Questo link è stato sospeso o sostituito. Chiedi al responsabile dell'attività il QR aggiornato.</p>
    </div>
  </div>
);

const Live = ({ quando }) => (
  <span data-testid="inspector-live-badge" className="px-3 py-1.5 rounded-full border border-emerald-400/40 text-emerald-300 text-xs font-semibold flex items-center gap-2">
    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
    Aggiornata alle {ora(quando)}
  </span>
);

const Intestazione = ({ dati, onAggiorna, carica }) => {
  const r = dati.ristorante;
  const dettagli = [r.nome !== r.insegna && r.nome, r.partita_iva && `P.IVA ${r.partita_iva}`, r.indirizzo].filter(Boolean).join(' · ');
  return (
    <header className="bg-slate-950 text-white">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 pt-6 pb-12 lg:pt-10 lg:pb-16">
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />Cartella per l'ispettore</span>
          <button type="button" onClick={onAggiorna} aria-label="Aggiorna" data-testid="inspector-refresh-btn"
            className="p-2 rounded-full border border-white/15 text-slate-300 hover:text-white hover:border-white/40 transition-colors"><RefreshCw className={`h-4 w-4 ${carica ? 'animate-spin' : ''}`} /></button>
        </div>
        <h1 data-testid="inspector-restaurant-name" className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none">{r.insegna || r.nome}</h1>
        {dettagli && <p className="text-sm text-slate-400 mt-2" data-testid="inspector-restaurant-details">{dettagli}</p>}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span data-testid="inspector-profile-title" className="px-3 py-1.5 rounded-full bg-white text-slate-950 text-xs font-bold">{dati.profilo.nome}</span>
          <Live quando={dati.aggiornata_il} />
        </div>
        <p className="mt-4 text-sm text-slate-300 max-w-xl">{dati.profilo.descrizione}. Tocca una cartella per vedere documenti e registri.</p>
      </div>
    </header>
  );
};

export default function CartellaIspettorePage({ token }) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [carica, setCarica] = useState(false);

  const leggi = useCallback(async () => {
    setCarica(true);
    try { setDati((await axios.get(`${API}/${token}`)).data); } catch { setErrore(true); } finally { setCarica(false); }
  }, [token]);

  useEffect(() => {
    document.title = 'Cartella per l\'ispettore';
    const meta = Object.assign(document.createElement('meta'), { name: 'robots', content: 'noindex, nofollow' });
    document.head.appendChild(meta);
    leggi();
    return () => meta.remove();
  }, [leggi]);

  const apri = (v) => {
    const [codice, periodo] = v.id.split(':');
    const url = v.tipo === 'registro' ? `${API}/${token}/registro/${codice}?periodo=${encodeURIComponent(periodo)}` : `${API}/${token}/documento/${v.id}`;
    window.open(url, '_blank', 'noopener');
  };

  if (errore) return <LinkNonValido />;
  if (!dati) return <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="inspector-loading"><Loader2 className="h-8 w-8 text-white animate-spin" /></div>;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="inspector-page">
      <Intestazione dati={dati} onAggiorna={leggi} carica={carica} />
      <main className="max-w-5xl mx-auto px-5 lg:px-8 -mt-6 pb-10 relative z-10"><CartellaVista dati={dati} onApri={apri} /></main>
      <footer className="max-w-5xl mx-auto px-5 lg:px-8 pb-10 text-xs text-slate-500 flex items-start gap-2">
        <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <p>Consultazione in sola lettura messa a disposizione dall'attività. I registri vengono creati al momento con i dati presenti (ultimi 3 mesi); i documenti restano sotto la responsabilità del titolare.</p>
      </footer>
    </div>
  );
}
